// Track-based profile setup. A mentor picks one of four tracks (their directory category)
// and the form shows that track's two paired fields, each with its own label, option list
// and seeded defaults.
//
// STORAGE: no new Firestore fields. Three tracks reuse advertising.examsTaught (Field A) +
// advertising.subjectsTaught (Field B). The Hobbies & Games track instead reuses the
// existing advertising.hobbies + advertising.sportsTaught, so the directory's Hobby/Game
// facets, the mentor-card line and the public-profile rows keep working unchanged.
//
// Every `defaults` entry MUST appear byte-identically in its own `options` list, or the
// chip renders unselected and the go-live gate can read the field as empty.
// tracks.test.ts asserts that.
import { HOBBY_OPTIONS } from './hobbies';
import { SPORT_OPTIONS } from './sports';

export type TrackKey = 'academic' | 'skills' | 'hobbies' | 'spiritual';
/** Which advertising.* array a track field reads and writes. */
export type TrackStorage = 'examsTaught' | 'subjectsTaught' | 'hobbies' | 'sportsTaught';

export const DEFAULT_TRACK: TrackKey = 'academic';

/** Order-preserving de-dupe — keeps first occurrence. */
const uniq = (a: string[]): string[] => [...new Set(a)];

// ── Academics ──────────────────────────────────────────────────────────────────
const ACADEMIC_EXAM_BASE = [
  'JEE Main', 'JEE Advanced', 'NEET UG', 'NEET PG', 'CUET', 'CLAT',
  'NDA', 'CDS', 'SSC CGL', 'SSC CHSL', 'UPSC CSE', 'CAT', 'XAT', 'GATE',
  'CA Foundation', 'CA Inter', 'CA Final', 'BITSAT', 'WBJEE', 'KCET',
  'MHT-CET', 'Class 10 Boards', 'Class 12 Boards',
  'Up to Class VI', 'Class VI to X',
  'IBPS PO', 'IBPS Clerk', 'SBI PO', 'RRB NTPC', 'RRB Group D',
  'Agniveer', 'AFCAT', 'CAPF / Police', 'State PSC', 'CTET',
  // These labels MUST stay byte-identical to EXAM_TAG_LABEL in
  // apps/functions/src/announcements/util.ts, or govt-job announcements notify zero mentors.
  'Govt Vacancies (Central)', 'Govt Vacancies (State)', 'UGC NET', 'KVS', 'State TET',
  'FMGE', 'DNB/DrNB',
  // Hotel management. 'NCHMCT JEE' is the notify-critical one — it MUST stay byte-identical
  // to EXAM_TAG_LABEL['nchmct-jee'], since notifyMentorsOfAnnouncement matches announcement
  // tags to mentors by that label against advertising.examsTaught. 'Hotel Management
  // Entrance' is the umbrella for the non-NTA tests (AIMA UGAT, IIHM eCHAT, MAH BHMCT CET,
  // state CETs) and has no tag of its own — it exists only as a picker label.
  'NCHMCT JEE', 'Hotel Management Entrance',
  'CLAT', 'Sainik School',
  // 'ICAR AIEEA' MUST stay byte-identical to EXAM_TAG_LABEL['icar-aieea'] (functions util.ts).
  'ICAR AIEEA',
  // Umbrella coaching offerings — the signal that a mentor does games / co-curriculars,
  // used to bucket them under Games / Hobbies (see mentorOffersGames/Hobbies).
  'Sports Coaching', 'Sports Quota / Trials', 'Extra-Curricular Activities',
  'French Language',
];

const ACADEMIC_SUBJECT_BASE = [
  'Mathematics', 'Physics', 'Chemistry', 'Biology', 'English',
  'Hindi', 'Sanskrit', 'French', 'History', 'Geography', 'Political Science',
  'Economics', 'Business Studies', 'Accountancy', 'Computer Science',
  'Sociology', 'Psychology', 'Legal Reasoning', 'GAT', 'General Studies',
  'Science', 'Social Science', 'Information Technology',
  'Applied Mathematics', 'Biotechnology', 'Physical Education', 'Home Science',
  'Informatics Practices', 'Entrepreneurship', 'Philosophy', 'Legal Studies',
  'Engineering Graphics', 'Environmental Science', 'Fine Arts',
  // Languages live with academic subjects (per brief).
  'German', 'Urdu', 'Punjabi', 'Bengali', 'Tamil', 'Telugu', 'Kannada',
  'Malayalam', 'Marathi', 'Gujarati', 'Odia', 'Assamese',
];

// Games and hobbies are offered under BOTH Exams and Subjects, so a mentor can tag them
// in either field. Deduped, and Golf arrives via SPORT_OPTIONS. The pickers are searchable,
// so the longer lists are fine.
export const ACADEMIC_EXAM_OPTIONS = uniq([...ACADEMIC_EXAM_BASE, ...SPORT_OPTIONS, ...HOBBY_OPTIONS]);
export const ACADEMIC_SUBJECT_OPTIONS = uniq([...ACADEMIC_SUBJECT_BASE, ...SPORT_OPTIONS, ...HOBBY_OPTIONS]);

// ── Career Skills ──────────────────────────────────────────────────────────────
// Public Speaking, Wellness/Yoga/Meditation and Aviation belong here, NOT in Hobbies.
export const CAREER_SKILL_OPTIONS = [
  'Corporate Trainer', 'Corporate Leadership Training', 'Leadership & Management',
  'Public Speaking', 'Communication Skills', 'Presentation Skills',
  'Personality Development', 'Soft Skills', 'Personal & Professional Skills Development',
  'Faculty Development', 'GD-PI Preparation', 'SSB Interview Preparation',
  'Personality & Interview Prep', 'Career Counselling', 'Admission Counselling',
  'Counselling', '21st Century Skills', 'Grooming & Skills Training', 'Life Coaching',
  'Wellness Facilitation', 'Wellness & Mindfulness Certification',
  'Yoga', 'Meditation', 'Mindfulness',
  'Aviation Technical Classes', 'Commercial Aviation Counselling',
  // Hospitality & culinary as a profession. A working chef / IHM trainer picks the Career
  // Skills track, so the track needs its own labels here — the qualification list alone
  // (QUALIFICATION_OPTIONS) can't satisfy the "what do you teach" gate. Leisure cooking is
  // separate and lives in HOBBY_OPTIONS.
  'Professional Cookery', 'Culinary Arts', 'Bakery & Patisserie',
  'Food Safety & Hygiene', 'Hotel Management & Hospitality Training',
  'Languages',
];

export const CAREER_AUDIENCE_OPTIONS = [
  'Corporate teams', 'Working professionals', 'College students', 'Job seekers',
  'School students', 'Individuals / 1-on-1', 'Workshops & seminars', 'Online', 'In-person',
];

// ── Spiritual ──────────────────────────────────────────────────────────────────
export const SPIRITUAL_PRACTICE_OPTIONS = [
  'Astrology Certification', 'Vedic Astrology Course', 'Numerology Course',
  'Tarot Reading Course', 'Vastu Shastra Course', 'Palmistry Course',
  'KP Astrology', 'Lal Kitab Course',
];

export const SPIRITUAL_SUBJECT_OPTIONS = [
  'Astrology', 'Vedic Astrology', 'Numerology', 'Tarot', 'Vastu Shastra',
  'Palmistry', 'Reiki', 'Meditation', 'Yoga', 'Spiritual Counselling',
  'KP Astrology', 'Lal Kitab',
];

export interface TrackFieldSpec {
  storage: TrackStorage;
  /** Form label. */
  label: string;
  /** Short label used on the public card / profile. */
  publicLabel: string;
  sub: string;
  noun: string;
  options: string[];
  defaults: string[];
}

export interface TrackSpec {
  key: TrackKey;
  /** Academics needs BOTH fields; every other track needs at least one. */
  requireBoth: boolean;
  fieldA: TrackFieldSpec;
  fieldB: TrackFieldSpec;
}

export const TRACKS: Record<TrackKey, TrackSpec> = {
  academic: {
    key: 'academic',
    requireBoth: true,
    fieldA: {
      storage: 'examsTaught', label: 'Exams you teach', noun: 'exams',
      publicLabel: 'Exams',
      sub: 'Pick every exam you coach for.',
      options: ACADEMIC_EXAM_OPTIONS, defaults: ['Class VI to X', 'Class 12 Boards'],
    },
    fieldB: {
      storage: 'subjectsTaught', label: 'Subjects you teach', noun: 'subjects',
      publicLabel: 'Subjects',
      sub: 'Pick every subject you teach, including languages.',
      options: ACADEMIC_SUBJECT_OPTIONS, defaults: ['General Studies'],
    },
  },
  skills: {
    key: 'skills',
    requireBoth: false,
    fieldA: {
      storage: 'examsTaught', label: 'Career skills you teach', noun: 'skills',
      publicLabel: 'Career skills',
      sub: 'Corporate training, public speaking, interview prep, wellness, aviation & more.',
      options: CAREER_SKILL_OPTIONS, defaults: ['Corporate Trainer'],
    },
    fieldB: {
      storage: 'subjectsTaught', label: 'Who you train', noun: 'audiences',
      publicLabel: 'Audience',
      sub: 'The audience and format you work with.',
      options: CAREER_AUDIENCE_OPTIONS, defaults: ['Working professionals'],
    },
  },
  hobbies: {
    key: 'hobbies',
    requireBoth: false,
    fieldA: {
      storage: 'hobbies', label: 'Hobbies you teach', noun: 'hobbies',
      publicLabel: 'Hobbies',
      sub: 'Music, dance, art, photography, cooking & more.',
      options: HOBBY_OPTIONS, defaults: ['Music'],
    },
    fieldB: {
      storage: 'sportsTaught', label: 'Games & sports you coach', noun: 'games',
      publicLabel: 'Games',
      sub: 'Cricket, football, badminton, athletics, chess, carrom & more.',
      options: SPORT_OPTIONS, defaults: ['Chess'],
    },
  },
  spiritual: {
    key: 'spiritual',
    requireBoth: false,
    fieldA: {
      storage: 'examsTaught', label: 'Practices you teach', noun: 'practices',
      publicLabel: 'Practices',
      sub: 'The courses and certifications you offer.',
      options: SPIRITUAL_PRACTICE_OPTIONS, defaults: ['Vedic Astrology Course'],
    },
    fieldB: {
      storage: 'subjectsTaught', label: 'Your specialities', noun: 'specialities',
      publicLabel: 'Specialities',
      sub: 'What you actually practise and advise on.',
      options: SPIRITUAL_SUBJECT_OPTIONS, defaults: ['Astrology'],
    },
  },
};

/** Experience seeded for every track. */
export const DEFAULT_YEARS = 1;

/** Order-insensitive equality — reordering chips must not read as "edited". */
export function sameValues(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const x = [...a].sort();
  const y = [...b].sort();
  return x.every((v, i) => v === y[i]);
}

/**
 * Values carried over from another track are meaningless under the new track's label
 * (academic exams shown as "Practices"). Treat a field with NO member of the new
 * track's option list as empty, so it gets that track's defaults instead.
 */
export function belongsToField(values: readonly string[], spec: TrackFieldSpec): boolean {
  return values.some((v) => spec.options.includes(v));
}

/** What a field should hold when switching to `track`, given its current value. */
export function resolveFieldOnSwitch(current: readonly string[], spec: TrackFieldSpec): string[] {
  return belongsToField(current, spec) ? [...current] : [...spec.defaults];
}

/**
 * Honest fallback label for an array that ISN'T one of the active track's two fields.
 * A Hobbies-track mentor whose content still sits in examsTaught (legacy data) must not
 * have those exams displayed under a "Hobbies" heading.
 */
export const STORAGE_LABEL: Record<TrackStorage, string> = {
  examsTaught: 'Exams',
  subjectsTaught: 'Subjects',
  hobbies: 'Hobbies',
  sportsTaught: 'Games',
};

/** Public-facing label for one array, given the mentor's track. */
export function publicLabelFor(track: TrackKey, storage: TrackStorage): string {
  const t = TRACKS[track];
  if (t.fieldA.storage === storage) return t.fieldA.publicLabel;
  if (t.fieldB.storage === storage) return t.fieldB.publicLabel;
  return STORAGE_LABEL[storage];
}

export interface TeachableArrays {
  examsTaught?: readonly string[] | undefined;
  subjectsTaught?: readonly string[] | undefined;
  hobbies?: readonly string[] | undefined;
  sportsTaught?: readonly string[] | undefined;
}

const filled = (a: readonly string[] | undefined): boolean => Array.isArray(a) && a.length > 0;

/**
 * Track-aware "what do you teach" rule for the public directory.
 *
 *   Academics — BOTH exams and subjects.
 *   Every other track — at least ONE of the four arrays.
 *
 * The non-academic branch deliberately counts examsTaught/subjectsTaught as well as
 * hobbies/sportsTaught. Restricting the Hobbies track to its own two arrays (as the brief
 * specified) would have hidden every Hobbies-category mentor live today, because their
 * content predates those fields and still sits in exams/subjects.
 *
 * MUST stay in lockstep with hasPublishableProfile() in
 * apps/functions/src/http/listPublicMentors.ts.
 */
export function meetsTeachableRule(track: TrackKey, v: TeachableArrays): boolean {
  if (track === 'academic') return filled(v.examsTaught) && filled(v.subjectsTaught);
  return filled(v.examsTaught) || filled(v.subjectsTaught) || filled(v.hobbies) || filled(v.sportsTaught);
}

/**
 * Best-guess track from what a mentor actually teaches.
 *
 * Used ONLY when the stored category is missing or was a retired catch-all — never to
 * override a category the mentor explicitly chose. Scores their exams/subjects against
 * each track's option lists and takes the clear winner.
 *
 * This exists because the retired `others` bucket turned out to hold academics and career
 * trainers, not hobbyists; blanket-mapping it to any single bucket mislabelled real
 * mentors on their public card.
 */
export function inferTrackFromContent(v: TeachableArrays): TrackKey | null {
  if (filled(v.hobbies) || filled(v.sportsTaught)) return 'hobbies';
  const values = [...(v.examsTaught ?? []), ...(v.subjectsTaught ?? [])];
  if (values.length === 0) return null;

  const score = (k: TrackKey): number => {
    const t = TRACKS[k];
    const opts = new Set([...t.fieldA.options, ...t.fieldB.options]);
    return values.filter((x) => opts.has(x)).length;
  };
  const ranked = (Object.keys(TRACKS) as TrackKey[])
    .map((k) => ({ k, n: score(k) }))
    .sort((a, b) => b.n - a.n);

  const top = ranked[0]!;
  if (top.n === 0) return null;
  // A tie is not a signal — leave it unlabelled rather than assert the wrong bucket.
  if (ranked[1] && ranked[1].n === top.n) return null;
  return top.k;
}
